'use client';

import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface DialogContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ============================================================================
// Context
// ============================================================================

const DialogContext = React.createContext<DialogContextValue | null>(null);

export function useDialogContext() {
  const context = React.useContext(DialogContext);
  if (!context) {
    throw new Error('useDialogContext must be used within Dialog');
  }
  return context;
}

// ============================================================================
// Dialog
// ============================================================================

export interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export function Dialog({
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  children,
}: DialogProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [isControlled, onOpenChange]
  );

  const contextValue: DialogContextValue = {
    open,
    onOpenChange: handleOpenChange,
  };

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
    </DialogContext.Provider>
  );
}

// ============================================================================
// DialogTrigger
// ============================================================================

export interface DialogTriggerProps {
  children: React.ReactElement;
  asChild?: boolean;
}

export function DialogTrigger({ children, asChild = false }: DialogTriggerProps) {
  const { onOpenChange } = useDialogContext();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onOpenChange(true);
  };

  // Always clone children if it's a valid element to avoid nested buttons
  if (React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: handleClick,
    } as React.HTMLAttributes<HTMLElement>);
  }

  // Only wrap in button if children is not an element (e.g., string)
  return (
    <button type="button" onClick={handleClick}>
      {children}
    </button>
  );
}

// ============================================================================
// DialogPortal
// ============================================================================

export interface DialogPortalProps {
  children: React.ReactNode;
  container?: HTMLElement;
}

export function DialogPortal({ children, container }: DialogPortalProps) {
  const { open } = useDialogContext();

  if (!open) return null;

  // In a real implementation, this would use ReactDOM.createPortal
  // For simplicity, we're rendering inline
  return <>{children}</>;
}

// ============================================================================
// DialogOverlay
// ============================================================================

export interface DialogOverlayProps extends React.HTMLAttributes<HTMLDivElement> {}

export function DialogOverlay({ className, ...props }: DialogOverlayProps) {
  const { onOpenChange } = useDialogContext();

  return (
    <div
      className={clsx(
        'dialog-overlay fixed inset-0 z-50',
        'bg-black/50 backdrop-blur-sm',
        'animate-in fade-in duration-200',
        className
      )}
      onClick={() => onOpenChange(false)}
      aria-hidden="true"
      {...props}
    />
  );
}

// ============================================================================
// DialogContent
// ============================================================================

export interface DialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: DialogSize;
  closeOnEscape?: boolean;
  closeOnOverlayClick?: boolean;
}

export function DialogContent({
  size = 'md',
  closeOnEscape = true,
  closeOnOverlayClick = true,
  className,
  children,
  ...props
}: DialogContentProps) {
  const { open, onOpenChange } = useDialogContext();
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open || !closeOnEscape) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeOnEscape, onOpenChange]);

  React.useEffect(() => {
    if (open) {
      const previouslyFocused = document.activeElement as HTMLElement;
      contentRef.current?.focus();

      return () => {
        previouslyFocused?.focus();
      };
    }
  }, [open]);

  if (!open) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full mx-4',
  };

  return (
    <>
      <DialogOverlay onClick={closeOnOverlayClick ? undefined : (e) => e.stopPropagation()} />
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={clsx(
          'dialog-content fixed left-1/2 top-1/2 z-50',
          '-translate-x-1/2 -translate-y-1/2',
          'w-full p-6',
          'bg-white dark:bg-gray-800',
          'border border-gray-200 dark:border-gray-700',
          'rounded-lg shadow-xl',
          'animate-in fade-in zoom-in-95 duration-200',
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {children}
      </div>
    </>
  );
}

// ============================================================================
// DialogHeader
// ============================================================================

export interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

export function DialogHeader({ className, ...props }: DialogHeaderProps) {
  return (
    <div
      className={clsx('dialog-header flex flex-col space-y-1.5 mb-4', className)}
      {...props}
    />
  );
}

// ============================================================================
// DialogTitle
// ============================================================================

export interface DialogTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {}

export function DialogTitle({ className, ...props }: DialogTitleProps) {
  return (
    <h2
      className={clsx(
        'dialog-title text-lg font-semibold',
        'text-gray-900 dark:text-gray-100',
        className
      )}
      {...props}
    />
  );
}

// ============================================================================
// DialogDescription
// ============================================================================

export interface DialogDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

export function DialogDescription({ className, ...props }: DialogDescriptionProps) {
  return (
    <p
      className={clsx('dialog-description text-sm text-gray-500 dark:text-gray-400', className)}
      {...props}
    />
  );
}

// ============================================================================
// DialogFooter
// ============================================================================

export interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

export function DialogFooter({ className, ...props }: DialogFooterProps) {
  return (
    <div
      className={clsx(
        'dialog-footer flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6',
        className
      )}
      {...props}
    />
  );
}

// ============================================================================
// DialogClose
// ============================================================================

export interface DialogCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

export function DialogClose({
  asChild = false,
  children,
  className,
  onClick,
  ...props
}: DialogCloseProps) {
  const { onOpenChange } = useDialogContext();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    onOpenChange(false);
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: handleClick,
    } as React.HTMLAttributes<HTMLElement>);
  }

  return (
    <button
      type="button"
      className={clsx(
        'dialog-close absolute right-4 top-4 p-1',
        'rounded-sm opacity-70 hover:opacity-100',
        'focus:outline-none focus:ring-2 focus:ring-gray-400',
        'transition-opacity',
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {children || (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      )}
    </button>
  );
}

// ============================================================================
// Modal (Convenience Wrapper)
// ============================================================================

export interface ModalProps {
  open?: boolean;
  onClose?: () => void;
  title?: string;
  description?: string;
  size?: DialogSize;
  showClose?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export function Modal({
  open = false,
  onClose,
  title,
  description,
  size = 'md',
  showClose = true,
  children,
  footer,
  className,
}: ModalProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent size={size} className={className}>
        {showClose && <DialogClose aria-label="Close" />}
        {(title || description) && (
          <DialogHeader>
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
        )}
        {children}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// ConfirmDialog
// ============================================================================

export interface ConfirmDialogProps {
  open?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  loading?: boolean;
}

export function ConfirmDialog({
  open = false,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  loading = false,
}: ConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm?.();
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium',
              'border border-gray-300 dark:border-gray-600',
              'bg-white dark:bg-gray-700',
              'text-gray-700 dark:text-gray-200',
              'hover:bg-gray-50 dark:hover:bg-gray-600',
              'disabled:opacity-50'
            )}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium',
              variant === 'destructive'
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-blue-600 text-white hover:bg-blue-700',
              'disabled:opacity-50'
            )}
          >
            {loading ? 'Loading...' : confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// AlertDialog
// ============================================================================

export interface AlertDialogProps {
  open?: boolean;
  onClose?: () => void;
  title?: string;
  description?: string;
  actionLabel?: string;
  type?: 'info' | 'success' | 'warning' | 'error';
}

export function AlertDialog({
  open = false,
  onClose,
  title,
  description,
  actionLabel = 'OK',
  type = 'info',
}: AlertDialogProps) {
  const icons = {
    info: (
      <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    success: (
      <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg className="w-12 h-12 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    error: (
      <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent size="sm" className="text-center">
        <div className="flex justify-center mb-4">{icons[type]}</div>
        {title && (
          <DialogTitle className="text-center mb-2">{title}</DialogTitle>
        )}
        {description && (
          <DialogDescription className="text-center">{description}</DialogDescription>
        )}

        <DialogFooter className="justify-center mt-6">
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'px-6 py-2 rounded-md text-sm font-medium',
              'bg-blue-600 text-white hover:bg-blue-700'
            )}
          >
            {actionLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// FullscreenDialog
// ============================================================================

export interface FullscreenDialogProps {
  open?: boolean;
  onClose?: () => void;
  title?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}

export function FullscreenDialog({
  open = false,
  onClose,
  title,
  children,
  actions,
}: FullscreenDialogProps) {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fullscreen-dialog fixed inset-0 z-50 bg-white dark:bg-gray-900">
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {title && (
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h2>
          )}

          <div className="flex items-center gap-2">{actions}</div>
        </div>

        <div className="flex-1 overflow-auto">{children}</div>
      </div>
    </div>
  );
}

// ============================================================================
// BottomSheet (Mobile-friendly dialog)
// ============================================================================

export interface BottomSheetProps {
  open?: boolean;
  onClose?: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  showHandle?: boolean;
}

export function BottomSheet({
  open = false,
  onClose,
  title,
  description,
  children,
  showHandle = true,
}: BottomSheetProps) {
  if (!open) return null;

  return (
    <>
      <div
        className="bottom-sheet-overlay fixed inset-0 z-50 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        className={clsx(
          'bottom-sheet fixed bottom-0 left-0 right-0 z-50',
          'bg-white dark:bg-gray-800',
          'rounded-t-xl shadow-xl',
          'animate-in slide-in-from-bottom duration-300',
          'max-h-[90vh] overflow-hidden'
        )}
      >
        {showHandle && (
          <div className="flex justify-center py-2">
            <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
          </div>
        )}

        <div className="px-4 pb-4">
          {(title || description) && (
            <div className="mb-4">
              {title && (
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {description}
                </p>
              )}
            </div>
          )}

          <div className="overflow-auto max-h-[70vh]">{children}</div>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// CommandDialog (For command palette style dialogs)
// ============================================================================

export interface CommandDialogProps {
  open?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
}

export function CommandDialog({ open = false, onClose, children }: CommandDialogProps) {
  React.useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="command-dialog-overlay fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        className={clsx(
          'command-dialog fixed left-1/2 top-[20%] z-50',
          '-translate-x-1/2',
          'w-full max-w-lg',
          'bg-white dark:bg-gray-800',
          'border border-gray-200 dark:border-gray-700',
          'rounded-lg shadow-xl',
          'animate-in fade-in zoom-in-95 slide-in-from-top-4 duration-200',
          'overflow-hidden'
        )}
      >
        {children}
      </div>
    </>
  );
}

// ============================================================================
// useDialog Hook
// ============================================================================

export interface UseDialogOptions {
  defaultOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface UseDialogReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  getDialogProps: () => {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  };
  getTriggerProps: () => {
    onClick: () => void;
  };
}

export function useDialog(options: UseDialogOptions = {}): UseDialogReturn {
  const { defaultOpen = false, onOpen, onClose } = options;
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const open = React.useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const close = React.useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const toggle = React.useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  const getDialogProps = React.useCallback(() => ({
    open: isOpen,
    onOpenChange: (newOpen: boolean) => {
      if (newOpen) {
        open();
      } else {
        close();
      }
    },
  }), [isOpen, open, close]);

  const getTriggerProps = React.useCallback(() => ({
    onClick: open,
  }), [open]);

  return {
    isOpen,
    open,
    close,
    toggle,
    getDialogProps,
    getTriggerProps,
  };
}
